import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, Facebook, Instagram } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  TOP_NAV,
  TOP_NAV_MEDIA,
  MAIN_NAV,
  SOCIALS,
  LOGO,
} from "../data/site";

/* ------------ Desktop dropdown (hover + focus) ------------- */
function NavDropdown({ label, items, testId, pillVariant = false }) {
  const [open, setOpen] = useState(false);
  const triggerCls = pillVariant
    ? "relative inline-flex items-center gap-1 px-3.5 py-2 text-sm font-semibold text-[#2b1c10]/85 hover:text-[#2b1c10] transition-colors"
    : "inline-flex items-center gap-1 text-xs font-medium text-[#2b1c10]/70 hover:text-[#2b1c10] transition-colors";

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="true"
        aria-expanded={open}
        className={triggerCls}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full pt-2 z-50"
        >
          <div
            role="menu"
            data-testid={`${testId}-menu`}
            className="min-w-[230px] bg-white border border-[#ebd9b5] rounded-xl shadow-[0_18px_40px_-12px_rgba(43,28,16,0.18)] py-2"
          >
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                role="menuitem"
                data-testid={`${testId}-item-${it.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                className={({ isActive }) =>
                  `block px-5 py-3 text-sm font-semibold transition-colors ${
                    isActive
                      ? "text-[#ed6a1a] bg-[#fdf8ec]"
                      : "text-[#2b1c10]/85 hover:text-[#ed6a1a] hover:bg-[#fdf8ec]"
                  }`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------ Header ------------- */
export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-[#ebd9b5] shadow-[0_1px_0_rgba(43,28,16,0.04)]"
          : "bg-[#fdf8ec]/90 backdrop-blur-sm"
      }`}
    >
      {/* ============ TOP THIN BAR (desktop only) ============ */}
      <div
        data-testid="header-top-bar"
        className="hidden lg:block border-b border-[#ebd9b5]/70"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-9">
            {/* Left — socials */}
            <div className="flex items-center gap-2">
              <a
                href={SOCIALS.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="topbar-social-facebook"
                aria-label="Facebook"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#ff7a1a] via-[#ed6a1a] to-[#c54508] text-white shadow-[0_3px_8px_-2px_rgba(237,106,26,0.55)] hover:scale-110 transition-transform"
              >
                <Facebook className="h-3.5 w-3.5" />
              </a>
              <a
                href={SOCIALS.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="topbar-social-instagram"
                aria-label="Instagram"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#ff7a1a] via-[#ed6a1a] to-[#c54508] text-white shadow-[0_3px_8px_-2px_rgba(237,106,26,0.55)] hover:scale-110 transition-transform"
              >
                <Instagram className="h-3.5 w-3.5" />
              </a>
            </div>

            {/* Right — info links + media dropdown + lang */}
            <nav className="flex items-center gap-5">
              {TOP_NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`topnav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className={({ isActive }) =>
                    `text-xs font-medium transition-colors ${
                      isActive
                        ? "text-[#ed6a1a]"
                        : "text-[#2b1c10]/70 hover:text-[#2b1c10]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              <NavDropdown
                label={TOP_NAV_MEDIA.label}
                items={TOP_NAV_MEDIA.items}
                testId="topnav-media"
              />

              <span
                data-testid="topnav-lang"
                className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#ed6a1a]/15 border border-[#ed6a1a]/30 text-[11px] font-bold tracking-[0.12em] text-[#ed6a1a]"
              >
                NL
              </span>
            </nav>
          </div>
        </div>
      </div>

      {/* ============ MAIN NAV BAR ============ */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 sm:h-28">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center shrink-0"
            aria-label="Mechelen Beach — Home"
          >
            <img
              src={LOGO}
              alt="Mechelen Beach"
              className="h-[88px] sm:h-[104px] lg:h-[120px] w-auto logo-dark"
            />
          </Link>

          {/* Desktop primary nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="mainnav-desktop">
            {MAIN_NAV.map((item) =>
              item.items ? (
                <NavDropdown
                  key={item.label}
                  label={item.label}
                  items={item.items}
                  testId={`mainnav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  pillVariant
                />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mainnav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className={({ isActive }) =>
                    `relative px-3.5 py-2 text-sm font-semibold transition-colors ${
                      isActive
                        ? "text-[#ed6a1a]"
                        : "text-[#2b1c10]/85 hover:text-[#2b1c10]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      {isActive && (
                        <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-[3px] rounded-full bg-[#ed6a1a]" />
                      )}
                    </>
                  )}
                </NavLink>
              )
            )}
          </nav>

          {/* Right — mobile trigger */}
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  data-testid="mobile-menu-trigger"
                  className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full bg-[#2b1c10] text-white hover:bg-[#3d2a1a] transition-colors"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-[88vw] sm:max-w-sm bg-[#fdf8ec] p-0 border-l border-[#ebd9b5]"
                data-testid="mobile-menu-sheet"
              >
                <SheetTitle className="sr-only">Navigatie</SheetTitle>
                <SheetDescription className="sr-only">
                  Hoofdnavigatie Mechelen Beach 2026
                </SheetDescription>
                <div className="flex flex-col h-full">
                  <div className="px-6 py-5 border-b border-[#ebd9b5]">
                    <Link to="/" className="flex items-center gap-3">
                      <img src={LOGO} alt="Mechelen Beach" className="h-14 w-auto logo-dark" />
                    </Link>
                  </div>

                  <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
                    <NavLink
                      to="/"
                      end
                      data-testid="mobile-nav-home"
                      className={({ isActive }) =>
                        `block px-4 py-3 text-base font-semibold rounded-xl transition-colors ${
                          isActive ? "bg-[#ed6a1a] text-white" : "text-[#2b1c10] hover:bg-white"
                        }`
                      }
                    >
                      Home
                    </NavLink>

                    {MAIN_NAV.map((item) =>
                      item.items ? (
                        <div key={item.label} className="mt-1">
                          <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#ed6a1a]">
                            {item.label}
                          </p>
                          {item.items.map((sub) => (
                            <NavLink
                              key={sub.to}
                              to={sub.to}
                              data-testid={`mobile-nav-${sub.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                              className={({ isActive }) =>
                                `block px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                                  isActive ? "bg-[#ed6a1a] text-white" : "text-[#2b1c10]/85 hover:bg-white"
                                }`
                              }
                            >
                              {sub.label}
                            </NavLink>
                          ))}
                        </div>
                      ) : (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                          className={({ isActive }) =>
                            `block px-4 py-3 text-base font-semibold rounded-xl transition-colors ${
                              isActive ? "bg-[#ed6a1a] text-white" : "text-[#2b1c10] hover:bg-white"
                            }`
                          }
                        >
                          {item.label}
                        </NavLink>
                      )
                    )}

                    <div className="mt-4 pt-4 border-t border-[#ebd9b5]">
                      <p className="px-4 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#ed6a1a]">
                        Meer
                      </p>
                      {TOP_NAV.map((item) => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          data-testid={`mobile-topnav-${item.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                          className={({ isActive }) =>
                            `block px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                              isActive ? "bg-[#ed6a1a] text-white" : "text-[#2b1c10]/85 hover:bg-white"
                            }`
                          }
                        >
                          {item.label}
                        </NavLink>
                      ))}
                      <p className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[#ed6a1a]">
                        {TOP_NAV_MEDIA.label}
                      </p>
                      {TOP_NAV_MEDIA.items.map((sub) => (
                        <NavLink
                          key={sub.to}
                          to={sub.to}
                          data-testid={`mobile-media-${sub.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                          className={({ isActive }) =>
                            `block px-4 py-2.5 text-sm font-semibold rounded-xl transition-colors ${
                              isActive ? "bg-[#ed6a1a] text-white" : "text-[#2b1c10]/85 hover:bg-white"
                            }`
                          }
                        >
                          {sub.label}
                        </NavLink>
                      ))}
                    </div>
                  </nav>

                  <div className="px-6 py-5 border-t border-[#ebd9b5]">
                    <div className="flex items-center gap-2">
                      <a
                        href={SOCIALS.facebook}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Facebook"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#2b1c10] hover:bg-[#ed6a1a] hover:text-white transition-colors"
                      >
                        <Facebook className="h-4 w-4" />
                      </a>
                      <a
                        href={SOCIALS.instagram}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Instagram"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#2b1c10] hover:bg-[#ed6a1a] hover:text-white transition-colors"
                      >
                        <Instagram className="h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
